/**
 * Rule-based paths section: list of `PathGroupCard`s with move/duplicate/delete
 * wiring against `draft.conditionalPathsGroups`.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import type {
  VoluumFlowUpdate,
  VoluumPathGroupUpdate,
} from '@trafficpulse/shared';

import { Button } from '@/components/ui/layout/Button';

import {
  PathGroupCard,
  deleteGroup,
  duplicateGroup,
  moveGroupDown,
  moveGroupUp,
} from '../../groups';

import {
  groupConditionsSelection,
  groupPathSelection,
  isGroupPathSelected,
  type PathsSelection,
} from './selection';
import SectionShell from '../SectionShell';
import { useRuleGroupsCollapse } from './use-rule-groups-collapse';

export interface RuleGroupsSectionProps {
  draft: VoluumFlowUpdate;
  onPatch: (mutator: (d: VoluumFlowUpdate) => void) => void;
  selection: PathsSelection;
  onSelect: (s: PathsSelection) => void;
}

const RuleGroupsSection: FC<RuleGroupsSectionProps> = ({
  draft,
  onPatch,
  selection,
  onSelect,
}) => {
  const groups = draft.conditionalPathsGroups ?? [];
  const {
    expandCard,
    toggleCardCollapsed,
    toggleExpandCollapseAll,
    isCardExpanded,
    allExpanded,
  } = useRuleGroupsCollapse(groups.length);

  const patchGroup = (
    idx: number,
    mutator: (g: VoluumPathGroupUpdate) => void,
  ) =>
    onPatch((d) => {
      const target = d.conditionalPathsGroups?.[idx];
      if (target) mutator(target);
    });

  const replaceGroups = (
    next: (gs: VoluumPathGroupUpdate[]) => VoluumPathGroupUpdate[],
  ) =>
    onPatch((d) => {
      d.conditionalPathsGroups = next(d.conditionalPathsGroups ?? []);
    });

  return (
    <SectionShell
      title="Rule-based paths"
      collapsible
      dense
      defaultExpanded={groups.length > 0}
      actions={
        groups.length > 0 ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 shrink-0 px-2 text-[11px]"
            onClick={() => toggleExpandCollapseAll(groups.length)}
            aria-label={
              allExpanded
                ? 'Collapse all rule-based path groups'
                : 'Expand all rule-based path groups'
            }
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
        ) : undefined
      }
    >
      {groups.length === 0 ? (
        <p className="text-[11px] italic leading-snug text-muted-foreground">
          No conditional groups on this flow.
        </p>
      ) : (
        <div className="space-y-1.5">
          {groups.map((g, idx) => (
            <PathGroupCard
              key={g.id ?? `group-${idx}`}
              group={g}
              groupIndex={idx}
              onPatch={(m) => patchGroup(idx, m)}
              isHeaderSelected={
                selection.kind === 'group-conditions' &&
                selection.groupIndex === idx
              }
              isExpanded={isCardExpanded(idx)}
              onToggleExpanded={() => toggleCardCollapsed(idx)}
              isPathSelected={(pathIdx) =>
                isGroupPathSelected(selection, idx, pathIdx)
              }
              onSelectHeader={() => {
                expandCard(idx);
                onSelect(groupConditionsSelection(idx));
              }}
              onSelectPath={(pathIdx) => {
                expandCard(idx);
                onSelect(groupPathSelection(idx, pathIdx));
              }}
              canMoveUp={idx > 0}
              canMoveDown={idx < groups.length - 1}
              onMoveUp={() => replaceGroups((gs) => moveGroupUp(gs, idx))}
              onMoveDown={() => replaceGroups((gs) => moveGroupDown(gs, idx))}
              onDuplicate={() => replaceGroups((gs) => duplicateGroup(gs, idx))}
              onDelete={() => replaceGroups((gs) => deleteGroup(gs, idx))}
            />
          ))}
        </div>
      )}
    </SectionShell>
  );
};

export default RuleGroupsSection;
